import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Card, CircularProgress, Dialog, DialogContent, DialogTitle, FormControl, Grid, IconButton, InputLabel, MenuItem, Select, Tooltip, Typography } from '@mui/material';
import { Delete as DeleteIcon, Error as ErrorIcon, CameraAlt as CameraAltIcon, UploadFile as UploadFileIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useDropzone } from 'react-dropzone';
import { useAddPhotoToProductMutation } from '../features/api/openFoodFacts/apiFoodFactsSlice';
import { setAlertMessage } from '../features/layoutSlice';
import Camera from './Camera';

function AddProductPhoto() {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { ean } = useParams();

  const fileInputRef = useRef(null);
  const isCameraActive = useSelector((state) => state.camera.camera);
  const [afterQuery, setAfterQuery] = useState(false);
  const [imagesWithErrors, setImagesWithErrors] = useState([]); // [ { image: file, error: 'error message' } ]
  const [uploadErrors, setUploadErrors] = useState([]);
  const [showUploadErrors, setShowUploadErrors] = useState([]);
  const [tmpImage, setTmpImage] = useState(null);
  const [totalImages, setTotalImages] = useState(0);
  const [imagesArray, setImagesArray] = useState([]);
  const [imagefieldArray, setImagefieldArray] = useState([]);
  const [isFileReady, setIsFileReady] = useState(false);
  const [fileToUpload, setFileToUpload] = useState(false);
  const [imagefield, setImagefield] = useState('front');
  const [open, setOpen] = useState(false);

  const [addPhotoToProduct, { isLoading }] = useAddPhotoToProductMutation();

  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      'image/*': ['.jpg, .jpeg, .png'],
    },
    multiple: false,
    onDrop: (acceptedFiles) => handleFileChange(acceptedFiles[0]),
  });

  useEffect(() => {
    if (afterQuery) {
      setAfterQuery(false);
      controlData();
    }
  }, [uploadErrors]);

  const handleDialog = () => {
    setIsFileReady(false);
    setOpen(!open);
  };

  const handleFileChange = (file) => {
    if (isCameraActive) dispatch({ type: 'camera/stop' });
    if (file.type === 'image/jpeg' || file.type === 'image/png') {
      setTmpImage(file);
      setIsFileReady(true);
    } else dispatch(setAlertMessage({ text: t('profile.editForm.fields.profilePicture.messages.upload.error'), type: 'error' }));
  };

  const handleAddImageToUpload = () => {
    setImagesArray([...imagesArray, tmpImage]);
    setImagefieldArray([...imagefieldArray, imagefield]);
    setTotalImages(totalImages + 1);
    setImagefield('front');
    setFileToUpload(true);
    setOpen(false);
  };

  const handleDeleteImage = (index) => {
    imagesArray.splice(index, 1);
    imagefieldArray.splice(index, 1);
    showUploadErrors.splice(index, 1);
    setTotalImages(totalImages - 1);
    if (totalImages === 1) setFileToUpload(false);
  };

  const listImageToUpload = imagesArray.map((image, index) => (
    <Grid container key={image?.name} sx={{ placeContent: 'center', mt: 1 }}>
      <Typography variant="body2" sx={{ mb: 1 }}>
        {imagefieldArray[index]}: {image?.name}
      </Typography>
      <IconButton onClick={() => handleDeleteImage(index)} sx={{ pt: 0 }}>
        <DeleteIcon />
      </IconButton>
      {showUploadErrors[index] && (
        <Tooltip title={showUploadErrors[index]} placement="right">
          <ErrorIcon color="error" />
        </Tooltip>
      )}
    </Grid>
  ));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const tmpErrors = [];
    const tmpImagesErrors = [];
    try {
      for (let i = 0; i < totalImages; i += 1) {
        const formData = new FormData();
        formData.append('code', ean);
        formData.append('filename', ean);
        formData.append('imagefield', imagefieldArray[i]);
        formData.append(`imgupload_${imagefieldArray[i]}`, imagesArray[i]);
        const response = await addPhotoToProduct({ formData }).unwrap(); // eslint-disable-line no-await-in-loop
        if (response.status === 'status not ok') {
          tmpErrors.push(response.error);
          tmpImagesErrors.push({ image: imagesArray[i], imagefield: imagefieldArray[i] });
        }
      }
      setUploadErrors([...uploadErrors, ...tmpErrors]);
      setImagesWithErrors([...imagesWithErrors, ...tmpImagesErrors]);
      setAfterQuery(true);
      cleanData();
    } catch (err) {
      console.log(err);
      dispatch(setAlertMessage({ text: t('products.messages.databaseError'), type: 'error' }));
    }
  };

  const controlData = () => {
    if (uploadErrors.length > 0) updateData();
    else {
      dispatch(setAlertMessage({ text: t('products.messages.addPhotoSuccess'), type: 'success' }));
    }
  };

  const updateData = () => {
    const tmpImagesArray = [];
    const tmpImagefieldArray = [];
    const tmpShowUploadErrors = [];
    imagesWithErrors.map((image, index) => {
      tmpImagesArray.push(image.image);
      tmpImagefieldArray.push(image.imagefield);
      tmpShowUploadErrors.push(uploadErrors[index]);
    });
    setImagesArray([...imagesArray, ...tmpImagesArray]);
    setImagefieldArray([...imagefieldArray, ...tmpImagefieldArray]);
    setShowUploadErrors([...showUploadErrors, ...tmpShowUploadErrors]);
    setTotalImages(imagesWithErrors.length);
    setImagesWithErrors([]);
    setUploadErrors([]);
    setFileToUpload(true);
  };

  const cleanData = () => {
    setImagesArray([]);
    setImagefieldArray([]);
    setShowUploadErrors([]);
    setTotalImages(0);
    setFileToUpload(false);
  };

  const handleOpenCamera = () => {
    dispatch({ type: 'camera/start' });
  };

  return (
    <>
      {isCameraActive && <Camera onCapture={handleFileChange} imagefield={imagefield} />}
      <Button variant="outlined" component="label" sx={{ mb: 2 }} onClick={handleDialog}>
        {t('products.form.addProduct.addProductPhoto.addPhoto')}
      </Button>
      <Dialog open={open} onClose={handleDialog} fullWidth maxWidth="sm">
        <DialogTitle>{t('products.form.addProduct.addProductPhoto.addPhoto')}</DialogTitle>
        <DialogContent>
          <Grid component="form" container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={12} sm={5}>
              <FormControl fullWidth required>
                <InputLabel id="imagefield-select-label">{t('products.form.addProduct.addProductPhoto.label')}</InputLabel> {/* TODO: da cambiare */}
                <Select labelId="imagefield-select-label" id="imagefield-select" name="imagefield" value={imagefield} label="Oggetto della foto" onChange={(e) => setImagefield(e.target.value)}>
                  <MenuItem value="front">{t('products.form.addProduct.addProductPhoto.select.front')}</MenuItem>
                  <MenuItem value="ingredients">{t('products.form.addProduct.addProductPhoto.select.ingredients')}</MenuItem>
                  <MenuItem value="nutrition">{t('products.form.addProduct.addProductPhoto.select.nutrition')}</MenuItem>
                  <MenuItem value="packaging">{t('products.form.addProduct.addProductPhoto.select.packaging')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {/* eslint-disable react/jsx-props-no-spreading */}
            <Grid item xs={12} sm={4.5} {...getRootProps()} sx={{ placeContent: 'center', display: 'flex' }}>
              <Button variant="outlined" component="label" htmlFor="productImage" onClick={(e) => e.stopPropagation()}>
                {t('products.form.addProduct.addProductPhoto.selectPhoto')}
                <UploadFileIcon />
                <input {...getInputProps()} type="file" id="productImage" accept=".jpg,.jpeg,.png" hidden required onChange={() => handleFileChange(fileInputRef.current.files[0])} ref={fileInputRef} />
              </Button>
            </Grid>
            {/* eslint-enable react/jsx-props-no-spreading */}
            <Grid item xs={12} sm={1} sx={{ placeContent: 'center', display: 'flex' }}>
              <Typography sx={{ alignSelf: 'center', pl: { xs: 0 } }}>{t('common.separator')}</Typography>
            </Grid>
            <Grid item xs={12} sm={1} sx={{ placeContent: 'center', display: 'flex' }}>
              <Button onClick={handleOpenCamera}>
                <CameraAltIcon />
              </Button>
            </Grid>
          </Grid>
          {isFileReady && <Typography sx={{ textAlign: 'center', pt: 2 }}>{tmpImage?.name}</Typography>}
          <Grid sx={{ placeContent: 'center', display: 'flex' }}>
            <Button variant="contained" onClick={handleAddImageToUpload} disabled={!isFileReady} sx={{ mt: 2 }}>
              {t('products.form.addProduct.addProductPhoto.addPhoto')}
            </Button>
          </Grid>
        </DialogContent>
      </Dialog>
      {fileToUpload && (
        <Card sx={{ width: '100%', height: '100%' }}>
          {listImageToUpload}
          {(isLoading && <CircularProgress />) || (
            <Button variant="contained" onClick={handleSubmit} sx={{ mb: 0.5 }}>
              {t('products.form.addProduct.addProductPhoto.uploadPhoto')}
            </Button>
          )}
        </Card>
      )}
    </>
  );
}

export default AddProductPhoto;
